import type { ReactNode } from 'react';
import type { ShapeKind } from '../doc/schema';
import { IconArrow, IconEllipse, IconEraser, IconLasso, IconLine, IconMarker, IconPen, IconRect } from '../ui/icons';
import { ColorPicker, SizePicker } from '../ui/ColorPicker';
import { usePane } from './context';
import { HandleControls, useFloatingCard } from './floating';
import { setToolSettings, useToolSettings, type PenMode } from './toolSettings';

const SHAPES: { kind: ShapeKind; label: string; icon: () => React.JSX.Element }[] = [
  { kind: 'rect', label: 'Rectangle', icon: IconRect },
  { kind: 'ellipse', label: 'Ellipse', icon: IconEllipse },
  { kind: 'line', label: 'Line', icon: IconLine },
  { kind: 'arrow', label: 'Arrow', icon: IconArrow },
];

const PENS: { mode: PenMode; label: string; icon: () => React.JSX.Element }[] = [
  { mode: 'pen', label: 'Pen', icon: IconPen },
  { mode: 'highlighter', label: 'Highlighter', icon: IconMarker },
  { mode: 'eraser', label: 'Eraser', icon: IconEraser },
  { mode: 'lasso', label: 'Lasso: select strokes (L)', icon: IconLasso },
];

/** Options card next to the toolbar for tools that have settings (shape, draw). */
export function ToolOptions() {
  const tool = usePane((s) => s.tool);
  if (tool === 'shape')
    return (
      <OptionsPanel title="Shape">
        <ShapeOptions />
      </OptionsPanel>
    );
  if (tool === 'draw')
    return (
      <OptionsPanel title="Draw">
        <DrawOptions />
      </OptionsPanel>
    );
  return null;
}

/**
 * The card itself. Docked beside the toolbar by default; dragging its title bar detaches it so
 * it floats anywhere in the pane (the position is remembered per device, shared by all tools).
 * The dock button, or a double-click on the bar, puts it back.
 */
function OptionsPanel({ title, children }: { title: string; children: ReactNode }) {
  const card = useFloatingCard<HTMLDivElement>('tool-options');
  return (
    <div ref={card.ref} className={`tool-options${card.floating ? ' is-floating' : ''}`} style={card.style} onPointerDown={(e) => e.stopPropagation()}>
      <div className="opt-handle" title="Drag to move these options anywhere (double-click docks them back)" {...card.handleProps}>
        <span className="opt-title">{title}</span>
        <HandleControls card={card} />
      </div>
      <div className="opt-body">{children}</div>
    </div>
  );
}

function ShapeOptions() {
  const s = useToolSettings((t) => t);
  return (
    <>
      <div className="opt-row">
        {SHAPES.map(({ kind, label, icon: Icon }) => (
          <button
            key={kind}
            type="button"
            className={`tb-btn${s.shapeKind === kind ? ' is-active' : ''}`}
            title={label}
            aria-label={label}
            onClick={() => setToolSettings({ shapeKind: kind })}
          >
            <Icon />
          </button>
        ))}
      </div>
      <div className="opt-label">Fill</div>
      <ColorPicker value={s.fill} onChange={(fill) => setToolSettings({ fill })} allowNone label="Fill" />
      <div className="opt-label">Stroke</div>
      <ColorPicker value={s.stroke} onChange={(stroke) => setToolSettings({ stroke })} allowNone label="Stroke" />
      <SizePicker value={s.strokeWidth} options={[1, 2, 3, 6, 10]} onChange={(strokeWidth) => setToolSettings({ strokeWidth })} />
    </>
  );
}

function DrawOptions() {
  const s = useToolSettings((t) => t);
  return (
    <>
      <div className="opt-row">
        {PENS.map(({ mode, label, icon: Icon }) => (
          <button
            key={mode}
            type="button"
            className={`tb-btn${s.penMode === mode ? ' is-active' : ''}`}
            title={label}
            aria-label={label}
            onClick={() => setToolSettings({ penMode: mode })}
          >
            <Icon />
          </button>
        ))}
      </div>
      {s.penMode === 'pen' && (
        <>
          <ColorPicker value={s.penColor} onChange={(penColor) => setToolSettings({ penColor })} label="Pen colour" />
          <SizePicker value={s.penSize} options={[2, 4, 8, 14, 24]} onChange={(penSize) => setToolSettings({ penSize })} />
        </>
      )}
      {s.penMode === 'highlighter' && (
        <>
          <ColorPicker
            value={s.highlighterColor}
            onChange={(highlighterColor) => setToolSettings({ highlighterColor })}
            label="Highlighter colour"
          />
          <SizePicker
            value={s.highlighterSize}
            options={[12, 24, 40]}
            onChange={(highlighterSize) => setToolSettings({ highlighterSize })}
          />
        </>
      )}
      {s.penMode === 'eraser' && (
        <SizePicker value={s.eraserSize} options={[12, 24, 48]} onChange={(eraserSize) => setToolSettings({ eraserSize })} />
      )}
      {s.penMode === 'lasso' && (
        <div className="opt-hint">Draw a loop around strokes to select them (Shift adds). Tap one to pick it. Drag a selected stroke to move; Delete removes.</div>
      )}
      <label className="opt-check" title="While drawing, a single finger never draws or pans; two fingers still pan and zoom. Other tools are not affected">
        <input type="checkbox" checked={s.penOnly} onChange={(e) => setToolSettings({ penOnly: e.target.checked })} />
        Pen only (fingers don't draw)
      </label>
    </>
  );
}
